import { useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRoute,
  HeadContent,
  Outlet,
  Scripts,
} from "@tanstack/react-router";
import { PostHogProvider } from "posthog-js/react";

import { ThemeProvider, Toaster } from "@kuzco/web/ui";

import "../styles/global.css";

import { createLocalStorage } from "../lib/local-storage";
import {
  catalystTrpc,
  createCatalystTrpcReactClient,
  createNubankTrpcReactClient,
  createRelayTrpcReactClient,
  nubankTrpc,
  relayTrpc,
} from "../trpc";

const THEME_INIT_SCRIPT = `(function(){var t=localStorage.getItem('ui-theme')||'system';if(t!=='light'&&t!=='dark'&&t!=='system'){localStorage.setItem('ui-theme','system');t='system'}if(t==='system')t=matchMedia('(prefers-color-scheme:dark)').matches?'dark':'light';document.body.className=t+' theme-ready'})()`;

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "NBDC" },
    ],
    links: [
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png",
      },
      { rel: "manifest", href: "/site.webmanifest" },
    ],
  }),
  shellComponent: RootDocument,
});

function RootDocument() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );
  const [nubankClient] = useState(() => createNubankTrpcReactClient());
  const [relayClient] = useState(() => createRelayTrpcReactClient());
  const [catalystClient] = useState(() => createCatalystTrpcReactClient());
  const [storage] = useState(() => createLocalStorage());

  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <script
          dangerouslySetInnerHTML={{
            __html: THEME_INIT_SCRIPT,
          }}
        />
        <PostHogProvider
          apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN || ""}
          options={{
            api_host: "/ingest",
            ui_host:
              import.meta.env.VITE_PUBLIC_POSTHOG_HOST ||
              "https://us.posthog.com",
            capture_exceptions: true,
            debug: import.meta.env.DEV,
          }}
        >
          <ThemeProvider storage={storage} storageKey="ui-theme">
            <QueryClientProvider client={queryClient}>
              <nubankTrpc.Provider
                client={nubankClient}
                queryClient={queryClient}
              >
                <relayTrpc.Provider
                  client={relayClient}
                  queryClient={queryClient}
                >
                  <catalystTrpc.Provider
                    client={catalystClient}
                    queryClient={queryClient}
                  >
                    <Outlet />
                    <Toaster />
                  </catalystTrpc.Provider>
                </relayTrpc.Provider>
              </nubankTrpc.Provider>
            </QueryClientProvider>
          </ThemeProvider>
        </PostHogProvider>
        <Scripts />
      </body>
    </html>
  );
}
